import {
  IComponent,
  IProject,
  IScene,
  TCanvasSidebar,
  IOnboardingSteps,
  IStreamResolution,
  IMediaInputDevices,
  TVideoMobileInputDevice,
  TEditorType,
} from '@/helpers/editor';
import { IIceConfig } from '@/helpers/user';
import { editorSettings } from '@/helpers/editor';

import { ITheme } from '@/helpers/theme';
import { ITransition } from '@/helpers/transition';
import { cloneDeep } from 'lodash';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';

@Module({ name: 'editor', namespaced: true })
export default class EditorModule extends VuexModule {
  components: IComponent[] = [];
  selectedComponent: IComponent = null;
  selectedEditor: TEditorType = null;
  projects: IProject[] = [];
  selectedProject: IProject = null;
  activeProject: IProject = null;
  scenes: IScene[] = [];
  selectedScene: IScene = null;
  componentUpdateUndoList: IComponent[][] = [];
  componentUpdateRedoList: IComponent[][] = [];

  streamResolutions: IStreamResolution[] = [
    { label: '720p - 30FPS', value: '720@30' },
    { label: '720p - 60FPS', value: '720@60' },
    { label: '1080p - 30FPS', value: '1080@30' },
    { label: '1080p - 60FPS', value: '1080@60' }
  ];

  allowedStreamResolutions: IStreamResolution[] = [
    { label: '720p - 30FPS', value: '720@30' },
    { label: '1080p - 30FPS', value: '1080@30' }
  ];

  resolutionsLoaded: boolean = false;

  zoom: number = 100;
  scale: number = 1;
  userToken: string = null;

  sidebarType: TCanvasSidebar = null;
  showSidebar: boolean = false;

  availableFonts: any = null;
  // prevent deactivating selected component on clicking elsewhere
  // https://github.com/mauricius/vue-draggable-resizable#preventdeactivation
  preventComponentDeactivate: boolean = false;

  onboardingSteps: IOnboardingSteps = {
    editor: 1,
    project: 1,
  };

  sceneToRemove: IScene = null;
  projectToRemove: IProject = null;
  hasUnsavedChange: boolean = false;

  projectToEdit: IProject = null;

  videoInputDevices: IMediaInputDevices[] = null;
  selectedVideoInputDevice: IMediaInputDevices = null;
  selectedFacingMode: TVideoMobileInputDevice = 'user';

  audioInputDevices: any[] = null;
  selectedAudioInputDevice: any = null;

  showSaveButtonTooltip: boolean = false;

  availableThemes: ITheme[] = null;
  availableTransitions: ITransition[] = null;

  showCanvasGrid: boolean = true;
  themeToInstall: ITheme = null;

  streamLabelData = null;
  streamLabelPoll = null;

  // peerConnections = {};
  iceConfig: IIceConfig = null;
  webcamStream: MediaStream = null;

  showVerticalCanvas: boolean = false;

  @Action
  setScale(scale: number) {
    this.context.commit('SET_SCALE', scale);
  }

  @Mutation
  SET_SCALE(scale: number) {
    this.scale = scale;
  }

  @Action
  setComponents(components: IComponent[]) {
    this.context.commit('SET_COMPONENTS', components);
  }

  @Mutation
  SET_COMPONENTS(components: IComponent[]) {
    this.components = components;
  }

  @Action
  addComponentUpdateUndo(components: IComponent[], clearRedoActions = true) {
    const undoList = cloneDeep(this.componentUpdateUndoList) || [];

    if (undoList.length > editorSettings.maxUndoSteps) {
      undoList.pop();
    }

    undoList.unshift(components);
    this.context.commit('SET_COMPONENT_UPDATE_UNDO_LIST', undoList);

    if (clearRedoActions) {
      this.clearComponentUpdateRedoList();
    }
  }

  @Mutation
  SET_COMPONENT_UPDATE_UNDO_LIST(componentUpdateUndoList: IComponent[][]) {
    this.componentUpdateUndoList = componentUpdateUndoList;
  }

  @Action
  undoComponentUpdateAction() {
    if (!this.componentUpdateUndoList.length) return;

    const componentUpdateUndoList = cloneDeep(this.componentUpdateUndoList) || [];

    this.addComponentUpdateRedo(cloneDeep(this.components));
    this.context.commit('SET_COMPONENTS', componentUpdateUndoList.shift());
    this.context.commit('SET_COMPONENT_UPDATE_UNDO_LIST', componentUpdateUndoList);
  }

  @Action
  clearComponentUpdateUndoList() {
    this.context.commit('CLEAR_COMPONENT_UPDATE_UNDO_LIST');
  }

  @Mutation
  CLEAR_COMPONENT_UPDATE_UNDO_LIST() {
    this.componentUpdateUndoList = [];
  }

  @Action
  addComponentUpdateRedo(components: IComponent[]) {
    const redoList = cloneDeep(this.componentUpdateRedoList) || [];

    if (redoList.length > editorSettings.maxRedoSteps) {
      redoList.pop();
    }

    redoList.unshift(components);
    this.context.commit('SET_COMPONENT_UPDATE_REDO_LIST', redoList);
  }

  @Mutation
  SET_COMPONENT_UPDATE_REDO_LIST(componentUpdateRedoList: IComponent[][]) {
    this.componentUpdateRedoList = componentUpdateRedoList;
  }

  @Action
  redoComponentUpdateAction() {
    if (!this.componentUpdateRedoList.length) return;

    const componentUpdateRedoList = cloneDeep(this.componentUpdateRedoList) || [];

    this.context.commit('SET_COMPONENTS', componentUpdateRedoList.shift());
    this.addComponentUpdateUndo(cloneDeep(this.components));
    this.context.commit('SET_COMPONENT_UPDATE_REDO_LIST', componentUpdateRedoList);
  }

  @Action
  clearComponentUpdateRedoList() {
    this.context.commit('CLEAR_COMPONENT_UPDATE_REDO_LIST');
  }

  @Mutation
  CLEAR_COMPONENT_UPDATE_REDO_LIST() {
    this.componentUpdateRedoList = [];
  }

  @Action
  clearComponentUpdateList() {
    this.context.commit('CLEAR_COMPONENT_UPDATE_UNDO_LIST');
    this.context.commit('CLEAR_COMPONENT_UPDATE_REDO_LIST');
  }

  @Action
  setSelectedComponent(component: IComponent) {
    this.context.commit('SET_SELECTED_COMPONENTS', component);
  }

  @Mutation
  SET_SELECTED_COMPONENTS(component: IComponent) {
    this.selectedComponent = component;
  }

  @Action
  setSelectedEditor(editor: TEditorType) {
    this.context.commit('SET_SELECTED_EDITOR', editor);
  }

  @Mutation
  SET_SELECTED_EDITOR(editor: TEditorType) {
    this.selectedEditor = editor;
  }

  @Action
  setProjects(projects: IProject[]) {
    this.context.commit('SET_PROJECTS', projects);
  }

  @Mutation
  SET_PROJECTS(projects: IProject[]) {
    this.projects = projects;
  }

  @Action
  setSelectedProject(project: IProject) {
    this.context.commit('SET_SELECTED_PROJECT', project);
  }

  @Mutation
  SET_SELECTED_PROJECT(projects: IProject) {
    this.selectedProject = projects;

    if (this.selectedProject?.settings?.showVerticalCanvas !== undefined) {
      this.showVerticalCanvas = this.selectedProject.settings.showVerticalCanvas;
    } else {
      // if not set, default to true
      this.showVerticalCanvas = true;
    }
  }

  @Action
  setActiveProject(project: IProject) {
    this.context.commit('SET_ACTIVE_PROJECT', project);
  }

  @Mutation
  SET_ACTIVE_PROJECT(projects: IProject) {
    this.activeProject = projects;
  }

  @Action
  setScenes(scenes: IScene[]) {
    this.context.commit('SET_SCENES', scenes);
  }

  @Mutation
  SET_SCENES(scenes: IScene[]) {
    this.scenes = scenes;
  }

  @Action
  setSelectedScene(scene: IScene) {
    this.context.commit('SET_SELECTED_SCENE', scene);
  }

  @Mutation
  SET_SELECTED_SCENE(scene: IScene) {
    this.selectedScene = scene;
  }

  @Action
  setUserToken(token: string) {
    this.context.commit('SET_USER_TOKEN', token);
  }

  @Mutation
  SET_USER_TOKEN(token: string) {
    this.userToken = token;
  }

  @Action
  setZoom(zoom: number) {
    this.context.commit('SET_ZOOM', zoom);
  }

  @Mutation
  SET_ZOOM(zoom: number) {
    this.zoom = zoom;
  }

  @Action
  setShowSidebar(value: boolean) {
    this.context.commit('SET_SHOW_SIDEBAR', value);
  }

  @Mutation
  SET_SHOW_SIDEBAR(value: boolean) {
    this.showSidebar = value;
  }

  @Action
  setSidebarType(sidebarType: TCanvasSidebar) {
    this.context.commit('SET_SIDEBAR_TYPE', sidebarType);
  }

  @Mutation
  SET_SIDEBAR_TYPE(sidebarType: TCanvasSidebar) {
    this.sidebarType = sidebarType;
  }

  @Action
  setAvailableFonts(availableFonts) {
    this.context.commit('SET_AVAILABLE_FONTS', availableFonts);
  }

  @Mutation
  SET_AVAILABLE_FONTS(availableFonts) {
    this.availableFonts = availableFonts;
  }

  @Action
  setPreventComponentDeactivate(preventComponentDeactivate: boolean) {
    this.context.commit('SET_PREVENT_COMPONENT_DEACTIVATE', preventComponentDeactivate);
  }

  @Mutation
  SET_PREVENT_COMPONENT_DEACTIVATE(preventComponentDeactivate: boolean) {
    this.preventComponentDeactivate = preventComponentDeactivate;
  }

  @Action
  setOnboardingSteps({ key, value }) {
    const data = cloneDeep(this.onboardingSteps);
    data[key] = this.onboardingSteps[key] + value;

    this.context.commit('SET_ONBOARDING_STEPS', data);
  }

  @Mutation
  SET_ONBOARDING_STEPS(value: IOnboardingSteps) {
    this.onboardingSteps = value;
  }

  @Action
  setSceneToRemove(scene: IScene) {
    this.context.commit('SET_SCENE_TO_REMOVE', scene);
  }

  @Mutation
  SET_SCENE_TO_REMOVE(scene: IScene) {
    this.sceneToRemove = scene;
  }

  @Action
  setProjectToRemove(project: IProject) {
    this.context.commit('SET_PROJECT_TO_REMOVE', project);
  }

  @Mutation
  SET_PROJECT_TO_REMOVE(project: IProject) {
    this.projectToRemove = project;
  }

  @Action
  setProjectToEdit(project: IProject) {
    this.context.commit('SET_PROJECT_TO_EDIT', project);
  }

  @Mutation
  SET_PROJECT_TO_EDIT(project: IProject) {
    this.projectToEdit = project;
  }

  @Action
  setHasUnsavedChange(hasUnsavedChange: boolean) {
    this.context.commit('SET_HAS_UNSAVED_CHANGE', hasUnsavedChange);
  }

  @Mutation
  SET_HAS_UNSAVED_CHANGE(hasUnsavedChange: boolean) {
    this.hasUnsavedChange = hasUnsavedChange;
  }

  @Action
  setAllowedStreamResolutions(allowedStreamResolutions) {
    this.context.commit('SET_ALLOWED_STREAM_RESOLUTIONS', allowedStreamResolutions);
  }

  @Mutation
  SET_ALLOWED_STREAM_RESOLUTIONS(allowedStreamResolutions) {
    this.allowedStreamResolutions = allowedStreamResolutions;
  }

  @Action
  setResolutionsLoaded(loaded) {
    this.context.commit('SET_RESOLUTIONS_LOADED', loaded);
  }

  @Mutation
  SET_RESOLUTIONS_LOADED(loaded) {
    this.resolutionsLoaded = loaded;
  }

  @Action
  setVideoInputDevices(videoInputDevices: IMediaInputDevices[]) {
    this.context.commit('SET_VIDEO_INPUT_DEVICES', videoInputDevices);
  }

  @Mutation
  SET_VIDEO_INPUT_DEVICES(videoInputDevices: IMediaInputDevices[]) {
    this.videoInputDevices = videoInputDevices;
  }

  @Action
  setSelectedVideoInputDevices(selectedVideoInputDevice: IMediaInputDevices) {
    this.context.commit('SET_SELECTED_VIDEO_INPUT_DEVICES', selectedVideoInputDevice);
  }

  @Mutation
  SET_SELECTED_VIDEO_INPUT_DEVICES(selectedVideoInputDevice: IMediaInputDevices) {
    this.selectedVideoInputDevice = selectedVideoInputDevice;
  }

  @Action
  setAudioInputDevices(audioInputDevices: IMediaInputDevices[]) {
    this.context.commit('SET_AUDIO_INPUT_DEVICES', audioInputDevices);
  }

  @Mutation
  SET_AUDIO_INPUT_DEVICES(audioInputDevices: IMediaInputDevices[]) {
    this.audioInputDevices = audioInputDevices;
  }

  @Action
  setSelectedAudioInputDevices(selectedAudioInputDevice: IMediaInputDevices) {
    this.context.commit('SET_SELECTED_AUDIO_INPUT_DEVICES', selectedAudioInputDevice);
  }

  @Mutation
  SET_SELECTED_AUDIO_INPUT_DEVICES(selectedAudioInputDevice: IMediaInputDevices) {
    this.selectedAudioInputDevice = selectedAudioInputDevice;
  }

  @Action
  setSelectedFacingMode(selectedFacingMode: TVideoMobileInputDevice) {
    this.context.commit('SET_SELECTED_FACING_MODE', selectedFacingMode);
  }

  @Mutation
  SET_SELECTED_FACING_MODE(selectedFacingMode: TVideoMobileInputDevice) {
    this.selectedFacingMode = selectedFacingMode;
  }

  @Action
  setShowSaveButtonTooltip(showSaveButtonTooltip: boolean) {
    this.context.commit('SET_SHOW_SAVE_BUTTON_TOOLTIP', showSaveButtonTooltip);
  }

  @Mutation
  SET_SHOW_SAVE_BUTTON_TOOLTIP(showSaveButtonTooltip: boolean) {
    this.showSaveButtonTooltip = showSaveButtonTooltip;
  }

  @Action
  setAvailableThemes(availableThemes: ITheme[]) {
    this.context.commit('SET_AVAILABLE_THEMES', availableThemes);
  }

  @Mutation
  SET_AVAILABLE_THEMES(availableThemes: ITheme[]) {
    this.availableThemes = availableThemes;
  }

  @Action
  setAvailableTransitions(availableTransitions: ITransition[]) {
    this.context.commit('SET_AVAILABLE_TRANSITIONS', availableTransitions);
  }

  @Mutation
  SET_AVAILABLE_TRANSITIONS(availableTransitions: ITransition[]) {
    this.availableTransitions = availableTransitions;
  }
  @Action
  setShowCanvasGrid(showCanvasGrid: boolean) {
    this.context.commit('SET_SHOW_CANVAS_GRID', showCanvasGrid);
  }

  @Mutation
  SET_SHOW_CANVAS_GRID(showCanvasGrid: boolean) {
    this.showCanvasGrid = showCanvasGrid;
  }

  @Action
  setThemeToInstall(themeToInstall: ITheme) {
    this.context.commit('SET_THEME_TO_INSTALL', themeToInstall);
  }

  @Mutation
  SET_THEME_TO_INSTALL(themeToInstall: ITheme) {
    this.themeToInstall = themeToInstall;
  }

  @Action
  setStreamLabelData(streamLabelData) {
    this.context.commit('SET_STREAM_LABEL_DATA', streamLabelData);
  }

  @Mutation
  SET_STREAM_LABEL_DATA(streamLabelData) {
    this.streamLabelData = streamLabelData;
  }

  @Action
  setStreamLabelPoll(streamLabelPoll) {
    this.context.commit('SET_STREAM_LABEL_POLL', streamLabelPoll);
  }

  @Mutation
  SET_STREAM_LABEL_POLL(streamLabelPoll) {
    this.streamLabelPoll = streamLabelPoll;
  }

  @Action
  setWebcamStream(webcamStream: MediaStream) {
    this.context.commit('SET_WEBCAM_STREAM', webcamStream);
  }

  @Mutation
  SET_WEBCAM_STREAM(webcamStream: MediaStream) {
    this.webcamStream = webcamStream;
  }

  @Action
  setIceConfig(iceConfig: IIceConfig) {
    this.context.commit('SET_ICE_CONFIG', iceConfig);
  }

  @Mutation
  SET_ICE_CONFIG(iceConfig: IIceConfig) {
    this.iceConfig = iceConfig;
  }

  @Action
  setShowVerticalCanvas(showVerticalCanvas: boolean) {
    this.context.commit('SET_SHOW_VERTICAL_EDITOR', showVerticalCanvas);
  }

  @Mutation
  SET_SHOW_VERTICAL_EDITOR(showVerticalCanvas: boolean) {
    this.showVerticalCanvas = showVerticalCanvas;
  }
}
